import { allBlocksOverlay } from './modules/all-blocks-overlay.ts';
import {
  addRevision,
  hideRevisionsUnlessSaving,
  populateRevisions,
  populateStudents,
  reviewStudent,
  saveStatus,
  switchToStudent,
} from './modules/assignment-ui.ts';
import { $ } from './modules/dom.ts';
import type { SiteMetadata } from './modules/evaluator-types.ts';
import {
  apiFetch,
  bootstrapFailed,
  describe,
  errorDetail,
  getJson,
  isOk,
  jsonIfOk,
  reportErrors,
} from './modules/fetch-helpers.ts';
import Login from './modules/login.ts';
import { pageConfig } from './modules/page-config.ts';
import {
  exitFullscreen,
  fetchPairingStatus,
  partnersLabel,
  showDriverChoice,
  showNavigatorCurtain,
  showPairingIndicator,
  showStashBanner,
} from './modules/pairing-ui.ts';
import { saveFloor } from './modules/save-floor.ts';
import { snapIDE } from './modules/snap-ide.ts';
import { RENDER_SCALE } from './modules/snap-pics-layout.ts';
import { type SnapRenderer, snapRenderer } from './modules/snap-render.ts';
import {
  type ConflictInfo,
  pageLocation,
  type Revision,
  ServerStorage,
} from './modules/storage.ts';

/*
 * Entry point for Snap! assignment pages (see views/layouts/snap.njk). Unlike
 * index.ts (Monaco + an evaluator) the "editor" here is an embedded Snap! IDE
 * running in an iframe (static/snap-embed.html). The project is one file,
 * project.xml, saved through the same storage layer as the JS/Java pages
 * (multi-MB media-bearing XML is fine: saves go to our own server, no
 * GitHub API size limits involved).
 *
 * Snap! runs in-browser via its own green flag, so there is no evaluator,
 * server submission, or test/result display here: just load on startup and
 * save (explicit button + autosave).
 */

/*
 * Save triggers — tune here. Snap! has several ways to "run" something, and
 * each can save-if-dirty. Saves are dirty-guarded and the git store dedupes
 * byte-identical content, so an unchanged re-run costs nothing — but a changed
 * one writes a fresh, whole-file (media-bearing) project.xml blob, so these
 * knobs are also the git-churn knobs. The greenFlag/blockRun/stop run events
 * come from patches/snap-runEvents.patch; leaving the page always saves.
 *
 * `floorMs` is the git-churn knob that matters most, because the cost of
 * this page turned out to be the *number* of versions rather than the size
 * of each. Measured on code-csp a month in: a saved version costs ~45KB on
 * disk (the deltas work fine — a project is multi-MB), but one student had
 * 682 versions of one assignment and e-card alone was 43% of a 628MB repo.
 * With every trigger firing freely that is a save every few seconds of
 * active work.
 *
 * So triggers are rate-limited rather than switched off: each one keeps its
 * meaning, but two saves never land within floorMs. A trigger inside the
 * window is *deferred* to the end of it, never dropped — dropping would mean
 * a student who green-flags and shuts the lid has work that reached nothing,
 * which the 30s autosave used to catch. The costs, which is why this is two
 * minutes and not ten: a lost tab exposes up to floorMs of work instead of
 * 30s (and the beforeunload save is best-effort at the best of times), and
 * the stored version is the state at the end of the window rather than the
 * instant of the run.
 */
const SAVE_ON = {
  greenFlag: true, // green flag clicked (runs all scripts)
  blockRun: true, // a top-level block/script clicked to evaluate it (Snap's Run analog)
  stop: false, // the red stop button
  // autosaveMs looks redundant next to the larger floorMs, but they govern
  // different things: the timer is the DETECTOR (how long freshly-dirty
  // work can sit unsaved after a quiet stretch — the lid-shut exposure),
  // the floor is the CADENCE (during continuous work, every trigger
  // collapses into one save per floorMs). Steady-state saves land every
  // ~floorMs; the first save after idle-then-edit lands within ~autosaveMs.
  autosaveMs: 30_000, // periodic save while there are unsaved edits; 0 disables the timer
  floorMs: 120_000, // minimum gap between saves; 0 disables the floor
};

const setup = async () => {
  const frame = $('#snap-frame') as HTMLIFrameElement;

  // Fullscreen toggle: make the embedded Snap! IDE fill the screen. Wired up
  // front, before anything that awaits the network, so it works even if login /
  // storage setup fails (e.g. in dev mode with no real GitHub). Fullscreens the
  // frame's container (the iframe fills it) to sidestep iframe permission policy.
  const fullscreenButton = $('.editor-toolbar .fullscreen-code');
  const fullscreenTarget = $('.snap-frame-container') as HTMLElement;
  if (fullscreenButton && fullscreenTarget) {
    (fullscreenButton as HTMLElement).onclick = () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        fullscreenTarget.requestFullscreen().catch((err) => {
          console.error('Fullscreen request failed:', err);
        });
      }
    };
    document.addEventListener('fullscreenchange', () => {
      (fullscreenButton as HTMLElement).innerText = document.fullscreenElement
        ? 'Exit fullscreen'
        : 'Fullscreen';
    });
  }

  const metadata = await getJson<SiteMetadata>('/metadata.json');
  // The config.json next to the page, or the inline copy a per-viewer page
  // (a student Snap! project) renders instead.
  const config = await pageConfig<{ files: string[] }>();

  // The Snap! project is a single file (defaults to project.xml).
  const [filename] = config.files;

  const login = new Login(metadata);
  const storage = await login.makeStorage();
  hideRevisionsUnlessSaving(storage);

  const ide = await snapIDE(frame);

  // Follow a live theme flip (plans/dark-mode.md). Snap! has its own
  // surface preference (the profile page's Snap! row; default: follow the
  // site), so listen for the 'snap' surface, not this page's own theme.
  // The embed constructed the IDE in the right theme already; refreshIDE()
  // re-serializes and reopens the project, so only flip live when there's
  // nothing unsaved — otherwise the new theme simply applies on next load.
  document.addEventListener('bhs:surfacethemechange', (e) => {
    const { surface, resolved } = (e as CustomEvent<{ surface: string; resolved: string }>).detail;
    if (surface !== 'snap' || ide.unsavedChanges()) return;
    if (resolved === 'dark') {
      ide.setDefaultTheme();
    } else {
      ide.setBrightTheme();
    }
    ide.refreshIDE();
  });

  // The serialized project as last loaded or saved. Snap!'s own dirty flag
  // is over-eager (plenty of gestures set it without changing the XML), so
  // "does this editor hold work of its own?" — the question the pushed
  // partner-save events (pair mode, below) have to answer — is byte
  // equality with this, not the flag.
  let lastSavedXml: string | null = null;

  const loadProject = (xml: string) => {
    ide.loadProjectXML(xml);
    ide.resetUnsavedChanges();
    lastSavedXml = xml;
    // Open with the full-size stage. The embed auto-fits the stage to the
    // iframe and its ratio only ever shrinks, so a project laid out while the
    // frame was briefly small would otherwise stay stuck on a tiny stage.
    ide.toggleStageSize(false);
  };

  // Save feedback shown next to the Save button (see snap.njk).
  const status = saveStatus();

  // When a background retry lands work buffered during a server outage.
  // Don't resetUnsavedChanges here: the flushed content may be older than
  // what's in the editor now; the normal autosave keeps handling that.
  storage.onBufferFlushed = (f) => {
    if (f.commit) {
      addRevision(f.commit);
      status.saved(f.commit.author.date);
    } else {
      // The buffered copy matched what was already saved — still say so,
      // rather than leaving a "recovering…" message up forever.
      status.noChanges();
    }
  };

  // Tracks the most recent in-flight save so autosave and the Save button can't
  // overlap and so a save is in a known state before navigation.
  let savePromise: Promise<unknown> = Promise.resolve();
  let saving = false;

  // Pair mode (plans/pair-code-sharing.md). While the driver-choice dialog
  // is up, autosave holds off (buffering instead) — every retry would just
  // 409 into the same dialog; the student's choice restarts saving.
  const pairStorage = storage instanceof ServerStorage ? storage : null;
  let pairPartnerLabel: string | null = null;
  // Who's who in the directory's history: the partners' usernames and this
  // student's own, as the commit events, revision lists and 409 details
  // carry them. Filled in once the pairing status arrives.
  let partnerUsernames = new Set<string>();
  let selfUsername: string | null = null;
  // Classify a directory move by its author. Only a PARTNER's move is a
  // driver question; this student's own (another tab, a background flush
  // of buffered work) and anyone else's (a reviewing teacher, an
  // underivable author) never raise the curtain or the choice — the bug
  // this closes was the dialog blaming an absent partner for the
  // student's own flushed save.
  const moverOf = (username: string | null): 'partner' | 'self' | 'other' => {
    if (username === null) return 'other';
    if (partnerUsernames.has(username)) return 'partner';
    return username === selfUsername ? 'self' : 'other';
  };
  let conflictPending = false;
  // Navigating: the partner is driving and the curtain is up. Tracked so
  // their continued saves refresh the project without rebuilding the
  // curtain out from under the student's cursor.
  let navigating = false;

  // Swap the IDE to the pair directory's saved project at `sha` (a partner's
  // newer save). loadProject resets unsavedChanges, so autosave stays quiet
  // until the student edits again. The directory sha is noted only once the
  // revision actually loaded: noting it after a failed load would arm the
  // next save to stomp the partner's newer work without a 409. Returns
  // whether the load happened.
  const refreshToRevision = async (sha: string): Promise<boolean> => {
    let xml: string;
    try {
      xml = await storage.loadAtRevision(filename, sha);
    } catch (e) {
      status.message(describe(e, `load ${pairPartnerLabel ?? 'your partner'}’s version`));
      return false;
    }
    loadProject(xml);
    pairStorage?.noteDirSha(sha);
    storage.revisions(filename).then(populateRevisions);
    return true;
  };

  // Taking over as driver is announced to the server, which fans it out
  // over the same event stream the partner's commits arrive on — so the
  // partner's page curtains (or raises the driver choice) the moment the
  // button is clicked, not when this student's first save happens to land.
  // Without the announcement the handoff rode entirely on that save, and
  // an actively-saving partner won the race every time: the curtain
  // takeover saves nothing at all (the IDE is clean at that moment), and
  // the conflict takeover's save kept bouncing off the partner's next
  // commit. A failed claim degrades to exactly that old save-landing path,
  // so it's reported but doesn't block anything.
  const claimDrive = () => {
    const { courseId, dir } = pageLocation();
    if (!courseId || !dir) return;
    apiFetch(`/api/code-driving/${courseId}/${dir}`, { method: 'POST' })
      .then(isOk)
      .catch((e: unknown) => status.message(describe(e, 'tell your partner you took over')));
  };

  // Navigating mode: the partner drives, this student watches. The curtain
  // blocks editing (so no divergent work can accrue), and underneath it the
  // freshness poll keeps loading the driver's saves — the IDE stays clean
  // under the curtain, which is exactly the poll's condition for following.
  // Taking over lifts the curtain; the IDE already holds the driver's latest
  // and the tracked dir sha is current, so the next save lands normally.
  const enterNavigating = () => {
    const partner = pairPartnerLabel ?? 'Your partner';
    navigating = true;
    status.message(`${partner} is driving — you're navigating`);
    showNavigatorCurtain({
      partnerLabel: partner,
      onTakeOver: () => {
        navigating = false;
        claimDrive();
        status.message('You are driving now');
      },
    });
  };

  // A save bounced off the partner's newer work: both students are editing,
  // and pair programming is one driver at a time — so this blocks the IDE
  // behind a modal until this student picks a role, rather than letting the
  // pair ping-pong conflicts at each other (or work on unnoticed for a
  // period with every save bouncing).
  const handleConflict = (conflict: ConflictInfo) => {
    if (moverOf(conflict.latestBy) !== 'partner') {
      // The directory moved, but not by the partner — this student's own
      // other tab or background flush, or a teacher. Not a driver question:
      // this editor's version wins and the other stays in the history. The
      // save that bounced is re-sent on top of the newer sha — once the
      // bouncing save has settled, since a 409 reaches here from inside it
      // (saving is still true, and save() would just return).
      pairStorage?.noteDirSha(conflict.latestSha);
      status.message(
        moverOf(conflict.latestBy) === 'self'
          ? 'Saved from another tab meanwhile — saving this version on top'
          : 'Someone else saved a version meanwhile — saving this version on top',
      );
      void savePromise.then(() => save());
      return;
    }
    conflictPending = true;
    showDriverChoice({
      partnerLabel: pairPartnerLabel ?? 'Your partner',
      onTakeOver: () => {
        // This student drives: their version saves on top of the partner's
        // (which stays one dropdown pick away in the history) and they keep
        // editing. The claim reaches the partner's page immediately (their
        // machine curtains, or raises this same choice, where "Keep
        // navigating" is the natural answer).
        claimDrive();
        void (async () => {
          // The sha captured when the dialog went up can be stale: events
          // are dropped while conflictPending, and the partner may have
          // kept saving while the dialog sat open. Ask for the true head
          // at click time so the takeover save lands on top of it instead
          // of 409ing straight back into this dialog. conflictPending
          // stays true across the fetch so an autosave tick keeps
          // buffering rather than racing the stale sha.
          let sha = conflict.latestSha;
          try {
            sha = (await pairStorage?.latestRevision())?.sha ?? sha;
          } catch {
            // Fall back to the captured sha: if it's stale the save 409s
            // into a fresh dialog carrying a fresher one.
          }
          conflictPending = false;
          pairStorage?.noteDirSha(sha);
          save();
        })();
      },
      onNavigate: () => {
        conflictPending = false;
        // Set this student's divergent version aside: stashed on this
        // computer (offered back by the stash banner on a later visit), its
        // buffered copy dropped so a background flush can't resurrect it
        // over the driver's work.
        try {
          localStorage.setItem(
            `conflict-stash:${pageLocation().pagePath}/${filename}`,
            ide.getProjectXML(),
          );
        } catch {
          // quota — the stash is best-effort
        }
        pairStorage?.discardBuffered([filename]);
        if (conflict.latestSha) {
          refreshToRevision(conflict.latestSha).then((loaded) => {
            // A failed load leaves this student's version on screen and
            // editable; the next save re-raises the choice.
            if (loaded) enterNavigating();
          });
        }
      },
    });
  };

  const save = () => {
    // Never write into a student's work we're only viewing.
    if (reviewStudent() || saving) return savePromise;
    // While the driver choice is unanswered a save would just 409 back into
    // the same dialog. The modal blocks the IDE, so no NEW work accrues, but
    // pre-modal content may still be unsaved — keep it safe in this browser
    // and keep saying so; a whole period of work must never ride on one
    // unanswered dialog.
    if (conflictPending) {
      storage.bufferNow([{ file: filename, content: ide.getProjectXML() }]);
      status.message('Not saved — you and your partner are both editing (choose in the dialog)');
      return savePromise;
    }
    saving = true;
    status.saving();
    savePromise = (async () => {
      try {
        const xml = ide.getProjectXML();
        const f = await storage.save(filename, xml);
        if (f.commit) {
          addRevision(f.commit);
          ide.resetUnsavedChanges();
          lastSavedXml = xml;
          status.saved(f.commit.author.date);
        } else if (f.conflict) {
          // Deliberately NOT resetUnsavedChanges: the editor still holds
          // the student's version until they choose.
          status.message('Not saved — your partner saved a newer version');
          handleConflict(f.conflict);
        } else if (f.buffered) {
          // Deliberately NOT resetUnsavedChanges: the autosave timer keeps
          // re-saving (and re-buffering the newest content) until a save
          // lands for real.
          if (f.authLost) {
            // Being logged out needs the student's attention (log in again),
            // and the status line is invisible in fullscreen.
            exitFullscreen();
            status.authLost();
          } else {
            status.message('Server unreachable — work kept in this browser, retrying…');
          }
        } else if (f.local) {
          ide.resetUnsavedChanges();
          status.message('Saved in this browser only');
        } else {
          // The store dedupes identical content: no new commit. A noop is
          // proof the editor content equals the stored HEAD, so clear the
          // dirty flag (same mid-save race as the commit branch above) —
          // otherwise Snap!'s many no-serialized-change gestures leave it
          // stuck on and the autosave timer noop-saves every 30s for as long
          // as the tab stays open.
          ide.resetUnsavedChanges();
          lastSavedXml = xml;
          status.noChanges();
        }
      } catch (e) {
        console.error('Snap! save failed:', e);
        status.failed();
      } finally {
        saving = false;
      }
    })();
    return savePromise;
  };

  // pageLocation, not a \w+ regex: course ids can contain hyphens, and a
  // failed parse silently fetched /api/undefined/students (an empty picker).
  const course = pageLocation().courseId ?? undefined;

  // Starter project for first open: use the committed project.xml at the
  // page path if the assignment ships one, otherwise seed from the empty
  // project Snap! already loaded (so most assignments need no checked-in XML).
  const starter = async () => {
    try {
      return await storage.loadFromWeb(filename);
    } catch {
      return ide.getProjectXML();
    }
  };

  const starterLoaded = storage
    .ensureStarter(filename, starter)
    .then((xml: string) => {
      loadProject(xml);
      // A buffered project from a previous visit (outage, or the teardown
      // buffer below) is what just loaded — load() prefers it — and is being
      // flushed in the background; make the recovery visible.
      if (storage.hasBufferedWork()) {
        status.message('Recovered unsaved work from your last visit — saving it now…');
      }
      if (storage.saves) {
        storage.revisions(filename).then(populateRevisions);
      }
    })
    // Only a load failure gets here (a missing starter is handled inside
    // `starter`): Snap!'s empty project is on screen over real saved work,
    // which must not pass for "nothing saved yet".
    .catch((e: unknown) => {
      status.message(describe(e, 'load your saved project'));
    });

  // A conflict stash from an earlier session ("Load their version" kept the
  // student's own unsaved version on this computer): offer it back, so the
  // stash is a way back rather than a write-only hole. Restore puts it in
  // the IDE and saves it — in pair mode that save can 409 into the ordinary
  // conflict flow, which is exactly the conversation to have.
  const stashKey = `conflict-stash:${pageLocation().pagePath}/${filename}`;
  if (!reviewStudent() && storage.saves) {
    starterLoaded.then(() => {
      const stashed = localStorage.getItem(stashKey);
      if (stashed === null) return;
      showStashBanner({
        onRestore: () => {
          localStorage.removeItem(stashKey);
          loadProject(stashed);
          save();
        },
        onDiscard: () => localStorage.removeItem(stashKey),
      });
    });
  }
  if (storage.saves) {
    // A failed picker load must not abort setup before saving is wired.
    await populateStudents(course, reviewStudent()).catch((e: unknown) =>
      status.message(describe(e, 'load the student list')),
    );
  }

  // "All blocks" (teachers only — the picker exists only for them — beside
  // the Live grid link): the snap-grid's all-blocks overlay over the project
  // now in the IDE, the reviewed student's or your own, unsaved edits
  // included. Rendering loads a project into an IDE, so it can't use the
  // live one: a second, hidden embed is created on first click.
  const overlayEl = $('#all-blocks') as HTMLDialogElement | null;
  const picker = $('#student-selector') as HTMLSelectElement | null;
  if (overlayEl && picker) {
    const overlay = allBlocksOverlay(overlayEl);
    let renderer: Promise<SnapRenderer> | null = null;
    const rendererReady = () => {
      if (!renderer) {
        const renderFrame = document.createElement('iframe');
        renderFrame.className = 'snap-render-frame';
        renderFrame.src = '/snap-embed.html';
        renderFrame.title = 'Snap! block renderer';
        renderFrame.setAttribute('aria-hidden', 'true');
        renderFrame.tabIndex = -1;
        document.body.appendChild(renderFrame);
        renderer = snapRenderer(renderFrame);
      }
      return renderer;
    };
    const link = document.createElement('a');
    link.id = 'all-blocks-link';
    link.className = 'grid-link';
    link.href = '#';
    link.textContent = 'All blocks';
    link.title =
      'Every custom block and script of this project, full size (blocks imported from libraries left out)';
    link.onclick = reportErrors('show the project’s blocks', async (e: MouseEvent) => {
      e.preventDefault();
      const reviewing = reviewStudent();
      const who = reviewing ? (picker.selectedOptions[0]?.text ?? reviewing) : 'Your project';
      await overlay.open(who, null, async () =>
        (await rendererReady()).definitionPics(ide.getProjectXML(), RENDER_SCALE),
      );
    });
    ($('#assignment-grid-link') ?? picker).after(link);
  }

  // Pair mode: if this user is paired for this assignment, show who with,
  // guard saves with the directory's latest-known sha (the driver-choice
  // dialog above), and keep an idle IDE quietly in step with the partner's
  // saves. Teachers reviewing see the indicator only.
  if (pairStorage) {
    const loc = pageLocation();
    if (loc.courseId && loc.dir) {
      const pairing = await fetchPairingStatus(loc.courseId, loc.dir, pairStorage.reviewing);
      if (pairing.unknown) status.message(pairing.unknown);
      if (pairing.paired) {
        pairPartnerLabel = partnersLabel(pairing.partners);
        partnerUsernames = new Set(pairing.partners.map((p) => p.username));
        selfUsername = pairing.self;
        showPairingIndicator(pairing, Boolean(pairStorage.reviewing));
        if (!pairing.detached && !pairStorage.reviewing) {
          // Baseline AFTER the starter flow so its ifAbsent commit (which
          // bypasses the save path's sha tracking) is already in the
          // history we read; only then do saves start carrying baseSha.
          await starterLoaded;
          await pairStorage.allRevisions().catch(() => {});
          pairStorage.enablePairMode();
          pairStorage.onConflict = handleConflict;

          // The directory moved to `sha`, saved by `by` (a username, or
          // null when the report doesn't say — the on-connect catch-up).
          // One driver at a time is the model, so a PARTNER's live save
          // doesn't just refresh a clean editor — it curtains it: this
          // student is now navigating and takes over explicitly, rather
          // than the pair trading silent updates fast enough to feel like
          // two drivers. A dirty editor raises the driver choice (both
          // were editing). A move by anyone else — our own save from
          // another tab or a background flush, a reviewing teacher —
          // follows silently when the editor is clean and otherwise just
          // yields: this editor's version wins on its next save, the
          // other staying in the history. Waiting out savePromise first
          // keeps our own commits (whose events also arrive here) from
          // reading as anyone's.
          //
          // The reported sha is then re-checked against the head as of
          // NOW, after that wait: a sha read before a save landed (the
          // sweep's probe, the catch-up on a reconnect) is stale, and
          // acting on it loaded the OLDER revision over work just saved
          // and left the page a sha behind, so the next save 409ed into
          // the driver choice. The probe also names the true author, so a
          // nameless report is classified by who really moved it.
          const onDirMoved = async (reported: string, _by: string | null) => {
            if (conflictPending) return;
            await savePromise;
            await pairStorage.pendingFlush; // a flush of our own buffer isn't a move either
            if (conflictPending || saving) return;
            if (reported === pairStorage.dirSha) return;
            let latest: Revision | null;
            try {
              latest = await pairStorage.latestRevision();
            } catch {
              return; // the next event or sweep tries again
            }
            if (conflictPending || saving) return;
            if (!latest || latest.sha === pairStorage.dirSha) return;
            const sha = latest.sha;
            const mover = moverOf(latest.username);
            const clean = !ide.unsavedChanges() || ide.getProjectXML() === lastSavedXml;
            if (mover === 'partner') {
              if (!clean) {
                handleConflict({ latestSha: sha, savedAt: null, latestBy: latest.username });
              } else if (await refreshToRevision(sha)) {
                if (navigating) {
                  status.message(`Updated with ${pairPartnerLabel}’s latest work`);
                } else {
                  enterNavigating();
                }
              }
              return;
            }
            if (clean) {
              if (await refreshToRevision(sha)) {
                status.message(
                  mover === 'self'
                    ? 'Updated with your save from another tab'
                    : 'Updated with the latest saved version',
                );
              }
            } else {
              pairStorage.noteDirSha(sha);
              status.message(
                mover === 'self'
                  ? 'Saved from another tab meanwhile — your next save here replaces it'
                  : 'Someone else saved a version meanwhile — your next save replaces it',
              );
            }
          };

          // A partner clicked "Take over as driver" (the claimDrive
          // broadcast). React NOW rather than when their first save lands
          // — the race that used to leave this student editing away,
          // oblivious, while the taker-over's every save bounced. Same
          // shape as onDirMoved: a clean editor curtains (it already holds
          // the head; the poll follows the new driver's saves underneath),
          // a dirty one raises the driver choice against the last known
          // head — both want to drive, so a human picks. Already
          // navigating, or already mid-choice, there's nothing to change.
          const onPartnerClaimed = async (claimer: string) => {
            if (conflictPending || navigating) return;
            await savePromise;
            await pairStorage.pendingFlush;
            if (conflictPending || saving || navigating) return;
            const clean = !ide.unsavedChanges() || ide.getProjectXML() === lastSavedXml;
            if (clean) {
              enterNavigating();
            } else {
              handleConflict({ latestSha: pairStorage.dirSha, savedAt: null, latestBy: claimer });
            }
          };

          // Fast path: the server pushes every commit in this directory
          // (modules/code-events.ts) the moment it lands, saying whose it
          // was. The first event on every (re)connect carries the current
          // sha with no author — the catch-up — so a deploy or wifi blip
          // costs nothing once EventSource redials on its own.
          let streamOk = false;
          const events = new EventSource(`/api/code-events/${loc.courseId}/${loc.dir}`);
          events.onopen = () => {
            streamOk = true;
          };
          events.onerror = () => {
            streamOk = false;
          };
          events.onmessage = (e) => {
            void (async () => {
              try {
                const { sha, username, driving } = JSON.parse(e.data) as {
                  sha: string | null;
                  username: string | null;
                  driving?: boolean;
                };
                const byPartner = username !== null && partnerUsernames.has(username);
                if (driving) {
                  // A takeover claim (no commit): our own arrives here too
                  // (this and every other tab), and only a partner's means
                  // anything.
                  if (byPartner) await onPartnerClaimed(username as string);
                } else if (sha) {
                  await onDirMoved(sha, username);
                }
              } catch {
                // Malformed or transient: the sweep below catches up.
              }
            })();
          };

          // The backstop sweep (the git store's own webhook-plus-sweep
          // pattern): the only mechanism while the stream is down, a slow
          // correctness check while it's up. A moved directory found here
          // is classified by its author like any other move.
          const PAIR_POLL_MS = 30_000;
          const SWEEP_EVERY = 10; // ≈ every 5 minutes with the stream up
          let tick = 0;
          setInterval(async () => {
            if (document.hidden || conflictPending || saving) return;
            tick++;
            if (streamOk && tick % SWEEP_EVERY !== 0) return;
            try {
              const latest = await pairStorage.latestRevision();
              if (latest) await onDirMoved(latest.sha, latest.username);
            } catch {
              // Transient poll failures just wait for the next tick.
            }
          }, PAIR_POLL_MS);
        }
      }
    }
  }

  // Explicit Save button.
  const saveButton = $('.editor-toolbar .save-code');
  if (saveButton) {
    (saveButton as HTMLElement).onclick = () => {
      if (reviewStudent()) {
        status.message("Read-only — viewing a student's copy");
      } else {
        save();
      }
    };
  }

  // Share permalink controls (plans/snap-share-permalink.md): a Share/Unshare
  // toggle just right of the Revisions menu. Sharing reveals the url + a
  // clipboard icon (click either to copy) and flips the button to Unshare.
  // Owner-only — hidden in browser-only mode and while reviewing.
  const shareControls = $('.editor-toolbar .share-controls');
  const loc = pageLocation();
  if (shareControls && storage.saves && !reviewStudent() && loc.courseId && loc.dir) {
    const shareApi = `/api/snap-share/${loc.courseId}/${loc.dir}`;
    const shareButton = $('.share-controls .share-code');
    const shareLink = $('.share-controls .share-link');
    const shareUrl = $('.share-controls .share-url');
    const shareCopy = $('.share-controls .share-copy');
    const shareStatus = $('.share-controls .share-status');

    let currentUrl: string | null = null;

    // A brief status line right by the controls, auto-cleared after a moment.
    let statusTimer: number | undefined;
    const flashStatus = (msg: string) => {
      shareStatus.textContent = msg;
      window.clearTimeout(statusTimer);
      statusTimer = window.setTimeout(() => {
        shareStatus.textContent = '';
      }, 1800);
    };

    const showShare = (shareId: string | null) => {
      currentUrl = shareId ? `${window.location.origin}/snap/shared/${shareId}` : null;
      if (currentUrl) shareUrl.textContent = currentUrl;
      (shareLink as HTMLElement).hidden = !currentUrl;
      shareButton.textContent = currentUrl ? 'Unshare' : 'Share';
    };

    const copyUrl = () => {
      if (!currentUrl) return;
      navigator.clipboard?.writeText(currentUrl).then(
        () => flashStatus('Copied'),
        () => {},
      );
    };
    shareUrl.addEventListener('click', copyUrl);
    shareCopy.addEventListener('click', copyUrl);

    // Reflect the current share state on load, then reveal the controls.
    apiFetch(shareApi)
      .then(jsonIfOk<{ shared: boolean; shareId: string | null }>)
      .then((s) => showShare(s.shared ? s.shareId : null))
      .catch((e: unknown) => {
        shareStatus.textContent = describe(e, 'check the share state');
      });
    (shareControls as HTMLElement).hidden = false;

    shareButton.onclick = async () => {
      if (currentUrl) {
        // Flip to Unshared only once the server agrees: a failed DELETE
        // leaves the public link live, and the control must say so.
        try {
          await apiFetch(shareApi, { method: 'DELETE' }).then(isOk);
          showShare(null); // the url + icon vanishing and the button flipping say it all
        } catch (e) {
          shareStatus.textContent = `${describe(e, 'unshare')} — the link is still live`;
        }
      } else {
        try {
          const r = await apiFetch(shareApi, { method: 'POST' }).then(
            jsonIfOk<{ shareId: string }>,
          );
          showShare(r.shareId);
        } catch (e) {
          shareStatus.textContent = describe(e, 'create a share link');
        }
      }
    };
  }

  // Saves are rate-limited: see modules/save-floor.ts for why, and
  // SAVE_ON.floorMs above for the interval.
  //
  // Two guards keep an unchanged project from costing a version. Snap!'s
  // own dirty flag, used here, is over-eager — plenty of its gestures mark
  // the project dirty without changing the serialized XML — so the git
  // store's content dedupe is the authoritative one: identical bytes make
  // no commit, and save() clears the flag on that noop. A false-dirty save
  // therefore costs one request and no version, and with the floor it costs
  // that at most once per floorMs rather than every 30s.
  const floor = saveFloor({
    floorMs: SAVE_ON.floorMs,
    isDirty: () => !reviewStudent() && ide.unsavedChanges(),
    save,
  });
  const saveIfDirty = (opts: { now?: boolean } = {}) => floor.request(opts);

  // Wrapped rather than passed directly: these are event listeners, so the
  // Event would arrive as saveIfDirty's options argument.
  const doc = frame.contentDocument;
  if (SAVE_ON.greenFlag) doc?.addEventListener('greenflag', () => saveIfDirty());
  if (SAVE_ON.blockRun) doc?.addEventListener('blockrun', () => saveIfDirty());
  if (SAVE_ON.stop) doc?.addEventListener('stopall', () => saveIfDirty());
  if (SAVE_ON.autosaveMs > 0) setInterval(() => saveIfDirty(), SAVE_ON.autosaveMs);

  // Leaving the page always flushes unsaved edits — the safety net, not a
  // tunable trigger, so it ignores the floor. The project is buffered into
  // localStorage FIRST: a fetch started during teardown can be aborted, and
  // the failure handler where saveAll's own buffering lives never runs on a
  // dead page — the synchronous buffer write is the part that can't be taken
  // away (the next visit flushes it if the save doesn't land). When the tab
  // is merely hidden the save completes normally and clears the buffer.
  const flushOnExit = () => {
    if (reviewStudent() || !ide.unsavedChanges()) return;
    storage.bufferNow([{ file: filename, content: ide.getProjectXML() }]);
    saveIfDirty({ now: true });
  };
  window.addEventListener('beforeunload', flushOnExit);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOnExit();
  });

  // Revisions dropdown loads an old project version, same as the code pages.
  const revisions = $('#file-revisions');
  if (revisions) {
    (revisions as HTMLSelectElement).onchange = (e) => {
      const sha = (e.target as HTMLSelectElement).value;
      storage.loadAtRevision(filename, sha).then(loadProject);
    };
  }

  // Teacher student-selector: reload the page viewing the chosen student.
  const studentSelector = $('#student-selector');
  if (studentSelector) {
    (studentSelector as HTMLSelectElement).onchange = (e) => {
      switchToStudent((e.target as HTMLSelectElement).value);
    };
  }
};

setup().catch((e) =>
  bootstrapFailed(e, () => {
    // A Snap! IDE with no save wiring must not look like one that saves:
    // make the frame inert (still visible) so nothing gets typed into it.
    const frame = $('#snap-frame') as HTMLIFrameElement | null;
    if (frame) {
      frame.inert = true;
      frame.style.opacity = '0.5';
    }
    const status = $('.editor-toolbar .save-status') as HTMLElement | null;
    if (status) status.textContent = `Not saving — page failed to load (${errorDetail(e)})`;
  }),
);
